import React, { useEffect, useMemo, useState } from 'react';
import clientAxios from '../../../clients/clientAxios';
import { Button, Form, Modal } from 'react-bootstrap';
import { notify } from 'react-notify-toast';
import moment from 'moment';
import 'moment-timezone';
import { useHistory } from 'react-router-dom';
import { TIMEZONE } from '../../../helpers/argenitianTimeZone';
import { getStudentLocation } from '../../../helpers/getStudentLocation';
import CountryFlag from '../list/CountryFlag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheck,
  faClock,
  faExclamationTriangle,
  faInbox,
  faMapMarkerAlt,
  faMoneyBillWave,
  faSearch,
  faSyncAlt,
  faUniversity
} from '@fortawesome/free-solid-svg-icons';

const SUBSCRIPTION_TYPE_BANK = 'Subscripciones Banco / Efectivo';
const SUBSCRIPTION_TYPE_ALL = 'Subscripciones';
const LOCATION_GUESS_NOTE = '* Basado en numero de telefono';

const optionsDate = [
  { value: 2, label: '2 días' },
  { value: 3, label: '3 días' },
  { value: 7, label: '1 semana' },
  { value: 14, label: '2 semanas' }
];

/** Build ISO start boundary for subscription query */
function buildQueryDate(rangeOption) {
  return moment().subtract(rangeOption.value, 'days').format('YYYY-MM-DDTHH:mm:ss.SSSZ');
}

/** Resolve initials for avatar circle */
function getInitials(givenName, familyName) {
  const firstInitial = (givenName || '').trim().charAt(0) || '·';
  const lastInitial = (familyName || '').trim().charAt(0) || '';
  return `${firstInitial}${lastInitial}`.toUpperCase();
}

/** Format date and time parts separately */
function formatSubscriptionDate(isoDate) {
  const zoned = moment(isoDate).tz(TIMEZONE);
  return {
    dayLabel: zoned.format('DD/MM/YYYY'),
    timeLabel: zoned.format('HH:mm')
  };
}

const Subscriptions = () => {
  const [studentsData, setStudentsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState(SUBSCRIPTION_TYPE_BANK);
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paidAmount, setPaidAmount] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [endDate, setEndDate] = useState(
    optionsDate.find((option) => option.value === 2)
  );
  const history = useHistory();

  const handleOpenModal = (student) => {
    setSelectedStudent(student);
    setPaidAmount(student?.amountToPay || student?.price || '');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
    setPaymentMethod(null);
    setPaidAmount('');
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  const handlePaidAmountChange = (event) => {
    setPaidAmount(event.target.value);
  };

  const handleSelectSubscriptionType = (nextType) => {
    setSubscriptionType(nextType);
  };

  const handleSelectEndDate = (nextValue) => {
    const selectedOption = optionsDate.find(
      (option) => option.value === parseInt(nextValue, 10)
    );
    if (selectedOption) {
      setEndDate(selectedOption);
    }
  };

  const getSubscriptions = async (silent) => {
    try {
      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      const calculatedDate = buildQueryDate(endDate);
      const queryParams = new URLSearchParams({ endDate: calculatedDate });
      if (subscriptionType === SUBSCRIPTION_TYPE_BANK) {
        queryParams.append('mode', 'precheck');
      }
      const subscriptionsResponse = await clientAxios.get(
        `/students/subscriptions?${queryParams}`
      );
      setStudentsData(subscriptionsResponse?.data || []);
    } catch {
      notify.show(
        'Ha ocurrido un error al intentar traer las subscripciones.',
        'error'
      );
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleSubscription = async (payload, method, amount) => {
    setIsLoading(true);
    try {
      const paymentUpdateResponse = await clientAxios.put(
        `/sellLogs/${payload._id}/payment-method`,
        { payment_method: method }
      );
      if (
        paymentUpdateResponse?.data?.message !==
        'Payment method updated successfully'
      ) {
        notify.show('No se pudo confirmar el método de pago.', 'error');
        setIsLoading(false);
        return;
      }
      const response = await clientAxios.post('/students/', {
        ...payload,
        paidAmount: Number(amount)
      });
      if (response?.data?.message?.includes('E-mail ya registrado')) {
        notify.show(response.data.message, 'error');
      } else {
        history.push(`/students/details/${response?.data?.id}`);
      }
    } catch (requestError) {
      notify.show(
        `Ha ocurrido un error al intentar inscribir al estudiante. ${requestError}`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChangeSubscriptionState = async (studentId, nextIsPending) => {
    try {
      const payload = { subscriptionPending: nextIsPending };
      const response = await clientAxios.patch(
        `/students/subscriptions/${studentId}`,
        payload
      );
      if (response?.data?.success) {
        notify.show('Suscripción actualizada exitosamente.', 'success');
        setStudentsData((previousStudents) =>
          previousStudents.map((student) =>
            student._id === studentId
              ? { ...student, subscriptionPending: nextIsPending }
              : student
          )
        );
      } else {
        notify.show('Hubo un problema actualizando la suscripción.', 'error');
      }
    } catch {
      notify.show('Error en la actualización de la suscripción.', 'error');
    }
  };

  useEffect(() => {
    getSubscriptions(false);
  }, [subscriptionType, endDate]);

  const summary = useMemo(() => {
    const totalCount = studentsData.length;
    const pendingCount = studentsData.filter(
      (student) => student?.subscriptionPending
    ).length;
    return {
      totalCount,
      pendingCount,
      readyCount: totalCount - pendingCount
    };
  }, [studentsData]);

  const visibleStudents = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return studentsData;
    }
    return studentsData.filter((student) => {
      const haystack = `${student?.name || ''} ${student?.surname || ''} ${
        student?.email || ''
      } ${student?.classroom || ''} ${student?.phone || ''}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [studentsData, searchQuery]);

  const isBankMode = subscriptionType === SUBSCRIPTION_TYPE_BANK;

  return (
    <div className="subs-page">
      <style>{`
        .subs-page {
          --subs-bg: #f9fafb;
          --subs-surface: #ffffff;
          --subs-line: #e7e5e4;
          --subs-line-soft: #f1f0ee;
          --subs-ink: #18181b;
          --subs-muted: #71717a;
          --subs-faint: #a1a1aa;
          --subs-slate: #1e293b;
          --subs-slate-deep: #0f172a;
          --subs-slate-line: #334155;
          --subs-sky: #0284c7;
          --subs-sky-deep: #0369a1;
          --subs-sky-soft: #38bdf8;
          --subs-emerald: #059669;
          --subs-emerald-deep: #047857;
          --subs-radius: 22px;
          background: var(--subs-bg);
          color: var(--subs-ink);
          font-family: "Satoshi", "Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          min-height: 100vh;
          padding: 40px 28px 72px;
        }
        .subs-shell { max-width: 1180px; margin: 0 auto; }
        .subs-topbar {
          display: grid;
          grid-template-columns: 1.35fr 1fr;
          gap: 28px;
          align-items: end;
          margin-bottom: 22px;
        }
        .subs-eyebrow {
          font-size: 12px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 700;
          color: var(--subs-sky);
          margin-bottom: 10px;
        }
        .subs-title {
          font-size: 34px;
          line-height: 1;
          letter-spacing: -0.04em;
          font-weight: 750;
          margin: 0 0 10px;
        }
        .subs-lede {
          font-size: 15px;
          line-height: 1.6;
          color: var(--subs-muted);
          max-width: 56ch;
          margin: 0;
        }
        .subs-metrics { display: flex; gap: 10px; margin-top: 18px; flex-wrap: wrap; }
        .subs-metric {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--subs-slate);
          border: 1px solid var(--subs-slate-line);
          color: #f1f5f9;
          border-radius: 999px;
          padding: 7px 13px;
          font-size: 13px;
          font-weight: 650;
          box-shadow: 0 10px 25px -12px rgba(0, 0, 0, 0.45);
        }
        .subs-metric b { font-variant-numeric: tabular-nums; color: #ffffff; }
        .subs-dot { width: 8px; height: 8px; border-radius: 999px; }
        .subs-dot-live { background: var(--subs-sky-soft); box-shadow: 0 0 0 4px rgba(56,189,248,0.25); animation: subsPulse 2.2s ease-in-out infinite; }
        .subs-dot-wait { background: #fbbf24; box-shadow: 0 0 0 4px rgba(251,191,36,0.22); }
        .subs-dot-done { background: #34d399; box-shadow: 0 0 0 4px rgba(52,211,153,0.22); }
        @keyframes subsPulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(0.82); opacity: 0.75; } }
        .subs-controls {
          background: var(--subs-slate);
          border: 1px solid var(--subs-slate-line);
          border-radius: var(--subs-radius);
          padding: 16px;
          box-shadow: 0 20px 40px -20px rgba(0, 0, 0, 0.45);
        }
        .subs-control-label {
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          font-weight: 750;
          color: #94a3b8;
          margin: 2px 2px 8px;
        }
        .subs-segment {
          display: flex;
          background: var(--subs-slate-deep);
          border: 1px solid var(--subs-slate-line);
          border-radius: 999px;
          padding: 4px;
          gap: 4px;
        }
        .subs-segment + .subs-control-label { margin-top: 14px; }
        .subs-seg-btn {
          flex: 1;
          border: 1px solid transparent;
          border-radius: 999px;
          background: transparent;
          color: #cbd5e1;
          font-size: 13px;
          font-weight: 650;
          padding: 8px 10px;
          cursor: pointer;
          white-space: nowrap;
          transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), background 0.25s, color 0.25s, box-shadow 0.25s;
        }
        .subs-seg-btn:hover { color: #ffffff; }
        .subs-seg-btn:active { transform: scale(0.98); }
        .subs-seg-btn.is-active {
          background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
          border-color: #0284c7;
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(2, 132, 199, 0.35);
        }
        .subs-seg-btn.is-active-emerald {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          border-color: #047857;
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(5, 150, 105, 0.35);
        }
        .subs-toolbar {
          display: flex;
          gap: 12px;
          align-items: center;
          margin: 6px 0 14px;
        }
        .subs-search {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--subs-slate);
          border: 1px solid var(--subs-slate-line);
          color: #ffffff;
          border-radius: 999px;
          padding: 10px 16px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .subs-search:focus-within { border-color: var(--subs-sky-soft); box-shadow: 0 0 0 3px rgba(56,189,248,0.35); }
        .subs-search svg { color: #94a3b8; font-size: 14px; }
        .subs-search input {
          border: 0;
          outline: 0;
          background: transparent;
          width: 100%;
          font-size: 14px;
          color: #ffffff;
        }
        .subs-search input::placeholder { color: #94a3b8; }
        .subs-refresh {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 1px solid var(--subs-slate-line);
          background: var(--subs-slate);
          border-radius: 999px;
          padding: 10px 16px;
          font-size: 13px;
          font-weight: 700;
          color: #ffffff;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
          transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), background 0.2s;
          white-space: nowrap;
        }
        .subs-refresh:hover { background: #334155; }
        .subs-refresh:active { transform: scale(0.98) translateY(1px); }
        .subs-refresh.is-spinning svg { animation: subsSpin 0.9s linear infinite; }
        @keyframes subsSpin { to { transform: rotate(360deg); } }
        .subs-panel {
          background: var(--subs-surface);
          border: 1px solid var(--subs-line);
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 24px 48px -32px rgba(24,24,27,0.28);
        }
        .subs-panel-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          background: var(--subs-slate);
          border-bottom: 1px solid var(--subs-slate-line);
        }
        .subs-panel-head strong { font-size: 14px; letter-spacing: -0.01em; color: #ffffff; }
        .subs-panel-head span {
          font-size: 12.5px;
          font-weight: 700;
          color: var(--subs-sky-soft);
          font-variant-numeric: tabular-nums;
          background: var(--subs-slate-deep);
          border: 1px solid var(--subs-sky);
          padding: 5px 12px;
          border-radius: 999px;
          white-space: nowrap;
        }
        .subs-col-head, .subs-row {
          display: grid;
          grid-template-columns: 118px 132px minmax(0,1.5fr) minmax(0,0.9fr) minmax(0,1.3fr) auto;
          gap: 16px;
          align-items: center;
        }
        .subs-col-head {
          padding: 12px 24px;
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          font-weight: 750;
          color: var(--subs-faint);
          background: #fbfbfa;
          border-bottom: 1px solid var(--subs-line-soft);
        }
        .subs-row {
          padding: 16px 24px;
          border-top: 1px solid var(--subs-line-soft);
          animation: subsRise 0.5s cubic-bezier(0.16,1,0.3,1) both;
          animation-delay: calc(var(--row-index, 0) * 55ms);
          transition: background 0.2s;
        }
        .subs-row:first-child { border-top: 0; }
        .subs-row:hover { background: #fcfcfa; }
        @keyframes subsRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .subs-date-day { font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; font-weight: 700; }
        .subs-date-time { font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: var(--subs-muted); }
        .subs-status {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border-radius: 999px;
          border: 1px solid transparent;
          font-size: 12.5px;
          font-weight: 750;
          padding: 7px 12px;
          cursor: pointer;
          transition: transform 0.2s cubic-bezier(0.16,1,0.3,1), background 0.2s, border-color 0.2s;
        }
        .subs-status:active { transform: scale(0.97); }
        .subs-status.is-pending { background: #fef3c7; color: #92400e; border-color: #f5dfa8; }
        .subs-status.is-ready { background: #e5f6ec; color: #146c43; border-color: #bfe6cd; }
        .subs-identity { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .subs-avatar {
          width: 38px; height: 38px; flex: none;
          border-radius: 999px;
          display: grid; place-items: center;
          background: var(--subs-slate);
          border: 2px solid var(--subs-slate-line);
          color: #fafafa;
          font-size: 13px; font-weight: 800; letter-spacing: 0.02em;
        }
        .subs-row.is-pending-row .subs-avatar { background: var(--subs-slate-deep); border-color: #d97706; color: #fbbf24; }
        .subs-who { min-width: 0; }
        .subs-name { font-size: 14px; font-weight: 700; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .subs-mail { font-size: 12.5px; color: var(--subs-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .subs-class {
          display: inline-flex;
          font-size: 13px;
          font-weight: 650;
          background: #f4f4f3;
          border: 1px solid var(--subs-line-soft);
          padding: 6px 11px;
          border-radius: 999px;
          max-width: 100%;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .subs-contact { min-width: 0; }
        .subs-phone { font-family: "JetBrains Mono", ui-monospace, Menlo, monospace; font-size: 13px; font-weight: 700; }
        .subs-location { font-size: 12px; color: var(--subs-muted); display: flex; align-items: center; gap: 5px; margin-top: 3px; }
        .subs-location img { border-radius: 2px; }
        .subs-guess-note { color: #b45309; font-size: 11px; font-weight: 600; display: flex; align-items: center; gap: 4px; margin-top: 2px; }
        .subs-enroll {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 1px solid #047857;
          cursor: pointer;
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: #ffffff;
          font-size: 13.5px;
          font-weight: 750;
          padding: 10px 18px;
          border-radius: 999px;
          box-shadow: 0 4px 12px rgba(5, 150, 105, 0.35);
          transition: transform 0.22s cubic-bezier(0.16,1,0.3,1), background 0.2s, box-shadow 0.2s;
          white-space: nowrap;
        }
        .subs-enroll:hover { background: linear-gradient(135deg, #047857 0%, #065f46 100%); box-shadow: 0 6px 16px rgba(5,150,105,0.45); }
        .subs-enroll:active { transform: translateY(1px) scale(0.98); }
        .subs-skeleton { padding: 8px 0; }
        .subs-skel-row {
          height: 66px;
          border-radius: 16px;
          margin: 10px 24px;
          background: linear-gradient(100deg, #efeeec 30%, #f8f7f5 50%, #efeeec 70%);
          background-size: 200% 100%;
          animation: subsShimmer 1.4s ease-in-out infinite;
        }
        @keyframes subsShimmer { 0% { background-position: 180% 0; } 100% { background-position: -80% 0; } }
        .subs-empty { padding: 64px 24px; text-align: left; display: grid; grid-template-columns: 56px 1fr; gap: 18px; align-items: start; }
        .subs-empty-icon {
          width: 56px; height: 56px; border-radius: 18px;
          display: grid; place-items: center;
          background: var(--subs-slate);
          border: 1px solid var(--subs-slate-line);
          color: var(--subs-sky-soft);
          font-size: 22px;
        }
        .subs-empty h3 { margin: 0 0 8px; font-size: 18px; letter-spacing: -0.02em; }
        .subs-empty p { margin: 0 0 16px; color: var(--subs-muted); font-size: 14px; line-height: 1.6; max-width: 52ch; }
        .subs-modal-card { border: 0; border-radius: 24px; overflow: hidden; }
        .subs-modal-card .modal-header, .subs-modal-card .modal-footer { border-color: #efeeec; }
        .subs-pay-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 14px; }
        .subs-pay {
          text-align: left;
          border: 1.5px solid var(--subs-line);
          background: #fff;
          border-radius: 18px;
          padding: 16px;
          cursor: pointer;
          transition: transform 0.2s cubic-bezier(0.16,1,0.3,1), border-color 0.2s, background 0.2s, box-shadow 0.2s;
        }
        .subs-pay:hover { border-color: #c9c8c6; }
        .subs-pay:active { transform: scale(0.98); }
        .subs-pay.is-selected { border-color: var(--subs-emerald); background: rgba(5,150,105,0.07); box-shadow: 0 0 0 3px rgba(5,150,105,0.18); }
        .subs-pay-icon {
          width: 40px; height: 40px; border-radius: 12px;
          display: grid; place-items: center;
          background: var(--subs-slate); color: #fff;
          margin-bottom: 12px;
        }
        .subs-pay.is-selected .subs-pay-icon { background: linear-gradient(135deg, #059669 0%, #047857 100%); }
        .subs-pay strong { display: block; font-size: 14px; margin-bottom: 4px; }
        .subs-pay small { color: var(--subs-muted); font-size: 12.5px; line-height: 1.5; }
        .subs-confirm {
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, #059669 0%, #047857 100%) !important;
          border: 1px solid #047857 !important;
          box-shadow: 0 4px 12px rgba(5, 150, 105, 0.35) !important;
          border-radius: 999px !important;
          padding: 10px 20px !important;
          font-weight: 750 !important;
        }
        .subs-confirm:hover:not(:disabled) { background: linear-gradient(135deg, #047857 0%, #065f46 100%) !important; }
        .subs-confirm:disabled { opacity: 0.45; }
        .subs-cancel { border-radius: 999px !important; padding: 10px 18px !important; font-weight: 650 !important; }
        @media (max-width: 1020px) {
          .subs-topbar { grid-template-columns: 1fr; }
          .subs-col-head { display: none; }
          .subs-col-head, .subs-row { grid-template-columns: 1fr; gap: 10px; }
          .subs-row { padding: 20px; }
          .subs-page { padding: 24px 14px 56px; }
          .subs-title { font-size: 28px; }
          .subs-pay-grid { grid-template-columns: 1fr; }
          .subs-empty { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="subs-shell">
        <div className="subs-topbar">
          <div>
            <div className="subs-eyebrow">Gestión · Inscripciones</div>
            <h2 className="subs-title">Suscripciones</h2>
            <p className="subs-lede">
              Revisa solicitudes recientes, cambia su estado y confirma la
              inscripción con el método de pago correspondiente.
            </p>
            <div className="subs-metrics">
              <span className="subs-metric">
                <span className="subs-dot subs-dot-live" />
                <b>{summary.totalCount}</b>&nbsp;en vista
              </span>
              <span className="subs-metric">
                <span className="subs-dot subs-dot-wait" />
                <b>{summary.pendingCount}</b>&nbsp;pendientes
              </span>
              <span className="subs-metric">
                <span className="subs-dot subs-dot-done" />
                <b>{summary.readyCount}</b>&nbsp;listas
              </span>
            </div>
          </div>

          <div className="subs-controls">
            <div className="subs-control-label">Origen</div>
            <div className="subs-segment" role="tablist">
              {[SUBSCRIPTION_TYPE_BANK, SUBSCRIPTION_TYPE_ALL].map((typeOption) => (
                <button
                  key={typeOption}
                  type="button"
                  className={`subs-seg-btn${subscriptionType === typeOption ? ' is-active' : ''}`}
                  onClick={() => handleSelectSubscriptionType(typeOption)}
                >
                  {typeOption === SUBSCRIPTION_TYPE_BANK ? 'Banco / Efectivo' : 'Todas'}
                </button>
              ))}
            </div>
            <div className="subs-control-label">Ventana de tiempo</div>
            <div className="subs-segment">
              {optionsDate.map((rangeOption) => (
                <button
                  key={rangeOption.value}
                  type="button"
                  className={`subs-seg-btn${endDate.value === rangeOption.value ? ' is-active-emerald' : ''}`}
                  onClick={() => handleSelectEndDate(rangeOption.value)}
                >
                  {rangeOption.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="subs-toolbar">
          <label className="subs-search">
            <FontAwesomeIcon icon={faSearch} />
            <input
              value={searchQuery}
              onChange={(searchEvent) => setSearchQuery(searchEvent.target.value)}
              placeholder="Buscar por nombre, email, comisión o teléfono…"
              aria-label="Buscar suscripciones"
            />
          </label>
          <button
            type="button"
            className={`subs-refresh${isRefreshing ? ' is-spinning' : ''}`}
            onClick={() => getSubscriptions(true)}
          >
            <FontAwesomeIcon icon={faSyncAlt} />
            Actualizar
          </button>
        </div>

        <div className="subs-panel">
          <div className="subs-panel-head">
            <strong>{isBankMode ? 'Pre-inscripciones por validar' : 'Historial de suscripciones'}</strong>
            <span>{visibleStudents.length} registros · {endDate.label}</span>
          </div>

          {isLoading ? (
            <div className="subs-skeleton" aria-label="Cargando suscripciones">
              {[0, 1, 2, 3, 4, 5].map((skeletonIndex) => (
                <div key={skeletonIndex} className="subs-skel-row" />
              ))}
            </div>
          ) : visibleStudents.length === 0 ? (
            <div className="subs-empty">
              <div className="subs-empty-icon">
                <FontAwesomeIcon icon={faInbox} />
              </div>
              <div>
                <h3>Sin resultados en esta vista</h3>
                <p>
                  No hay suscripciones para los filtros elegidos. Amplía la
                  ventana de tiempo, cambia el origen o limpia la búsqueda para
                  ver más solicitudes.
                </p>
                <button
                  type="button"
                  className="subs-refresh"
                  onClick={() => {
                    setSearchQuery('');
                    getSubscriptions(true);
                  }}
                >
                  <FontAwesomeIcon icon={faSyncAlt} />
                  Reintentar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="subs-col-head">
                <span>Fecha</span>
                <span>Estado</span>
                <span>Estudiante</span>
                <span>Comisión</span>
                <span>Contacto</span>
                <span>{isBankMode ? 'Acción' : ''}</span>
              </div>
              <div className="subs-list">
                {visibleStudents.map((student, rowIndex) => {
                  const dateParts = formatSubscriptionDate(student?.updatedAt);
                  const isPending = Boolean(student?.subscriptionPending);
                  const studentLocation = getStudentLocation(student);
                  return (
                    <div
                      key={student?._id || rowIndex}
                      className={`subs-row${isPending ? ' is-pending-row' : ''}`}
                      style={{ '--row-index': rowIndex }}
                    >
                      <div>
                        <div className="subs-date-day">{dateParts.dayLabel}</div>
                        <div className="subs-date-time">{dateParts.timeLabel} hs</div>
                      </div>
                      <div>
                        <button
                          type="button"
                          className={`subs-status ${isPending ? 'is-pending' : 'is-ready'}`}
                          onClick={() =>
                            handleSelectChangeSubscriptionState(student._id, !isPending)
                          }
                          title="Cambiar estado"
                        >
                          <FontAwesomeIcon icon={isPending ? faClock : faCheck} />
                          {isPending ? 'Pendiente' : 'Ok'}
                        </button>
                      </div>
                      <div className="subs-identity">
                        <div className="subs-avatar">
                          {getInitials(student?.name, student?.surname)}
                        </div>
                        <div className="subs-who">
                          <div className="subs-name">
                            {student?.name} {student?.surname}
                          </div>
                          <div className="subs-mail">{student?.email}</div>
                        </div>
                      </div>
                      <div>
                        <span className="subs-class">{student?.classroom || '—'}</span>
                      </div>
                      <div className="subs-contact">
                        <div className="subs-phone">{student?.phone || '—'}</div>
                        <div className="subs-location">
                          <CountryFlag country={studentLocation.country?.split(' *')[0]} />
                          <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '10px' }} />
                          {studentLocation.country || '—'}
                          {studentLocation.region ? ` · ${studentLocation.region}` : ''}
                        </div>
                        {studentLocation.isGuessed && (
                          <span className="subs-guess-note">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            {LOCATION_GUESS_NOTE}
                          </span>
                        )}
                      </div>
                      <div>
                        {isBankMode && (
                          <button
                            type="button"
                            className="subs-enroll"
                            onClick={() => handleOpenModal(student)}
                          >
                            Inscribir
                            <FontAwesomeIcon icon={faArrowRight} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered contentClassName="subs-modal-card">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '18px', letterSpacing: '-0.02em' }}>
            Confirmar inscripción
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '22px 24px' }}>
          <div style={{ fontSize: '14px', color: '#52525b' }}>
            {selectedStudent ? (
              <>
                Inscribiendo a{' '}
                <strong style={{ color: '#18181b' }}>
                  {selectedStudent?.name} {selectedStudent?.surname}
                </strong>{' '}
                · {selectedStudent?.classroom}
              </>
            ) : (
              'Selecciona el método de pago para continuar.'
            )}
          </div>
          <div className="subs-pay-grid">
            <button
              type="button"
              className={`subs-pay${paymentMethod === 'bank' ? ' is-selected' : ''}`}
              onClick={() => handlePaymentMethodChange('bank')}
            >
              <div className="subs-pay-icon">
                <FontAwesomeIcon icon={faUniversity} />
              </div>
              <strong>Transferencia</strong>
              <small>Acredita banco y genera comprobante automático.</small>
            </button>
            <button
              type="button"
              className={`subs-pay${paymentMethod === 'cash' ? ' is-selected' : ''}`}
              onClick={() => handlePaymentMethodChange('cash')}
            >
              <div className="subs-pay-icon">
                <FontAwesomeIcon icon={faMoneyBillWave} />
              </div>
              <strong>Efectivo</strong>
              <small>Registra cobro en caja del día actual.</small>
            </button>
          </div>
          {paymentMethod && (
            <Form.Group controlId="subsPaidAmount" style={{ marginTop: '18px' }}>
              <Form.Label>Importe abonado</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={paidAmount}
                onChange={handlePaidAmountChange}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="subs-cancel" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button
            className="subs-confirm"
            disabled={!paymentMethod || !paidAmount}
            onClick={() => {
              if (paymentMethod) {
                handleSubscription(selectedStudent, paymentMethod, paidAmount);
                handleCloseModal();
              } else {
                notify.show('Debe seleccionar un método de pago', 'error');
              }
            }}
          >
            Confirmar inscripción
            <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '4px' }} />
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Subscriptions;
